/**
 * Browser-side structured logging, forwarded to Axiom through the API.
 *
 * There is deliberately no Axiom token here: anything in a JS bundle is public,
 * and a leaked ingest token lets a stranger flood (and bill) the dataset. Events
 * are POSTed to `/api/client-logs`, which holds the token server-side.
 *
 * Built after an incident where an admin saw an empty Employees page: the API
 * answered a healthy `200 {employees: []}`, so Sentry recorded nothing. What was
 * missing was plain context — which account the session was on, and how many
 * rows came back. Both are captured here.
 */
import axios from "axios";

const ENDPOINT =
  (import.meta.env.VITE_API_URL || "https://server.saasaki.com/api") + "/client-logs";

const FLUSH_INTERVAL_MS = 4000;
const MAX_BATCH = 25;
const MAX_QUEUE = 200;

export type ClientLogEvent = {
  kind: string;
  [key: string]: unknown;
};

let queue: Record<string, unknown>[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** Identity of the signed-in user, attached to every event once known. */
let actor: { actorType?: string; actorId?: string; actorEmail?: string; actorName?: string } = {};

export function setLogActor(next: typeof actor) {
  actor = next ?? {};
}

function send(events: Record<string, unknown>[]) {
  if (!events.length) return;
  const payload = JSON.stringify({ events });

  // sendBeacon survives tab close / navigation, which plain fetch does not.
  if (navigator.sendBeacon) {
    try {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      if (ok) return;
    } catch {
      /* fall through to axios */
    }
  }

  // Bare axios, not axiosInstance — the interceptor logs failures, and routing
  // that back through here would loop.
  axios.post(ENDPOINT, { events }, { headers: { "Content-Type": "application/json" } }).catch(() => {
    /* logging must never surface to the user */
  });
}

export function flushLogs() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const batch = queue;
  queue = [];
  send(batch);
}

export function logEvent(event: ClientLogEvent) {
  try {
    if (queue.length >= MAX_QUEUE) queue.shift();

    queue.push({
      ...actor,
      ...event,
      clientTime: new Date().toISOString(),
      url: window.location.pathname + window.location.search,
      userRole: localStorage.getItem("userRole") ?? undefined,
    });

    if (queue.length >= MAX_BATCH) {
      flushLogs();
    } else if (!timer) {
      timer = setTimeout(flushLogs, FLUSH_INTERVAL_MS);
    }
  } catch {
    /* never let logging throw into app code */
  }
}

/** Uncaught errors, promise rejections, and a final flush on unload. */
export function installGlobalLogHandlers() {
  window.addEventListener("error", (e) => {
    logEvent({
      kind: "client_error",
      message: e.message,
      source: e.filename,
      line: e.lineno,
      column: e.colno,
      stack: e.error instanceof Error ? e.error.stack : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    logEvent({
      kind: "client_unhandled_rejection",
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });

  // pagehide is the reliable one on mobile Safari; visibilitychange covers tab switches.
  window.addEventListener("pagehide", flushLogs);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushLogs();
  });
}
